import { useId } from 'react';

import { FIELD_LIMITS } from '../lib/fieldLimits.js';
import type { LinkMode, OrderIntake } from '../lib/ordersApi.js';

import { NO_AUTOFILL } from './ui.js';

/**
 * Outgoing links — intake v2, section B.
 *
 * The section carries one home page link and allows up to two more. Three per
 * site, and the form cannot offer a fourth.
 *
 * **The home page link's destination is the buyer's** (change order of 5 Sep
 * 2026, round 2). It was fixed text showing the main URL, because it *was* the
 * main URL; it is now a Target URL field that starts at the main URL and keeps
 * tracking it until the buyer edits it, after which their value stands. An
 * agency may want the flagship link pointing at a specific page or another
 * asset. What did not change is that there is exactly one home page link, and
 * it is placed on the built site's home page.
 *
 * **Mentions-only is parked, not deleted.** The section used to lead with a
 * two-way mode choice, and the mentions-only half of it is hidden from the form
 * (change order of 5 Sep 2026) until the operator has evidence those builds
 * work. Nothing below the form moved: `LinkMode` keeps both values, the server
 * still accepts and renders `mentions_only`, and `linkFieldsFrom` still knows
 * how to send it. What changed is that the form has no way to choose it, so
 * every intake it submits is `link_out` — which means every new build carries
 * at least the home page link, deliberately. Bringing the option back is
 * restoring a control, not rebuilding a feature.
 *
 * Where a secondary link lands is fixed copy rather than a control: the page is
 * chosen when the site is made, and offering a box for it would be asking a
 * question whose answer is not used.
 */

/** One home page link plus at most two more. */
export const MAX_SECONDARY_LINKS = 2;
export const MAX_LINKS_PER_SITE = MAX_SECONDARY_LINKS + 1;

export const MAX_LINK_WORDING = FIELD_LIMITS.linkWording;

export interface SecondaryLinkRow {
  url: string;
  wording: string;
}

export interface OutgoingLinksValue {
  mode: LinkMode;
  /**
   * Where the home page link points, once the buyer has said so. **Null means
   * the field is still tracking the Main URL**, which is the state every form
   * starts in. Null and `""` are different answers: the empty string is a
   * required field the buyer cleared, and the server refuses it rather than
   * quietly falling back.
   */
  homeUrl: string | null;
  /** Wording for the pinned home page link. Blank means "use the target name". */
  homeWording: string;
  secondary: SecondaryLinkRow[];
}

export function emptySecondaryRow(): SecondaryLinkRow {
  return { url: '', wording: '' };
}

export function emptyOutgoingLinks(): OutgoingLinksValue {
  return { mode: 'link_out', homeUrl: null, homeWording: '', secondary: [] };
}

/**
 * The home page link's destination, as the field shows it: the main URL until
 * the buyer edits it, their own value from then on. One helper, so the input
 * and the payload cannot disagree about what the link points at.
 */
export function homeUrlOf(value: OutgoingLinksValue, mainUrl: string): string {
  return value.homeUrl ?? mainUrl.trim();
}

/** The parts of the intake this section owns. */
export type LinkIntakeFields = Pick<
  OrderIntake,
  'linkMode' | 'homeLinkUrl' | 'homeLinkWording' | 'secondaryLinks'
>;

/**
 * The link answers, as the intake carries them.
 *
 * Rows with no address are dropped, and blank wording is omitted rather than
 * sent as `""` — an empty preference and no preference are the same thing, and
 * only one of them is honest. The home target is the exception: it is **always**
 * sent, blank included, because the intake reads an absent `homeLinkUrl` as "use
 * the main URL". Omitting a field the buyer had just cleared would answer, on
 * their behalf, the one question they took an answer away from. Mentions-only
 * sends none of it, and is kept here against the mode coming back: the form
 * cannot currently reach this branch.
 */
export function linkFieldsFrom(value: OutgoingLinksValue, mainUrl: string): LinkIntakeFields {
  if (value.mode === 'mentions_only') return { linkMode: 'mentions_only' };

  const secondaryLinks = value.secondary
    .map((row) => ({ url: row.url.trim(), wording: row.wording.trim() }))
    .filter((row) => row.url !== '')
    .map((row) => (row.wording === '' ? { url: row.url } : { url: row.url, wording: row.wording }));

  const homeWording = value.homeWording.trim();

  return {
    linkMode: 'link_out',
    homeLinkUrl: homeUrlOf(value, mainUrl).trim(),
    ...(homeWording === '' ? {} : { homeLinkWording: homeWording }),
    ...(secondaryLinks.length > 0 ? { secondaryLinks } : {}),
  };
}

/**
 * One extra row's errors, **one per field** - C-61, pre-launch fixes.
 *
 * It was one message for the row, printed below both boxes and below the
 * "Placed on a suitable inside page." line, so a refusal about the URL sat
 * under the anchor text as much as under the URL. The home block already had
 * an error per field; the extra rows now match it.
 */
export interface SecondaryRowErrors {
  url?: string | undefined;
  wording?: string | undefined;
}

export interface OutgoingLinksErrors {
  homeUrl?: string | undefined;
  homeWording?: string | undefined;
  /** Keyed by row index, then by field. */
  secondary?: Record<number, SecondaryRowErrors> | undefined;
  /** Anything about the set as a whole — the cap, or the mode. */
  links?: string | undefined;
}

export interface OutgoingLinksFieldProps {
  value: OutgoingLinksValue;
  onChange: (value: OutgoingLinksValue) => void;
  /** The main URL the home page link starts at, and tracks until it is edited. */
  mainUrl: string;
  /** Used as the placeholder for wording: blank means we use the name. */
  targetName: string;
  errors?: OutgoingLinksErrors | undefined;
}

const INPUT_CLASS =
  'mt-1 block w-full rounded-control border-0 px-3 py-2 text-fg shadow-sm ring-1 ring-inset ring-rule-strong placeholder:text-fg-faint focus:ring-2 focus:ring-inset focus:ring-blue-ink sm:text-sm';

export function OutgoingLinksField({
  value,
  onChange,
  mainUrl,
  targetName,
  errors,
}: OutgoingLinksFieldProps): React.JSX.Element {
  const groupId = useId();
  const atCap = value.secondary.length >= MAX_SECONDARY_LINKS;

  function updateRow(index: number, patch: Partial<SecondaryLinkRow>): void {
    onChange({
      ...value,
      secondary: value.secondary.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    });
  }

  return (
    <fieldset>
      <legend className="text-sm font-semibold text-fg-muted">Outgoing links</legend>
      <p className="mt-1 text-sm text-fg-muted">Up to three per site.</p>

      {errors?.links ? <p className="mt-2 text-sm text-danger">{errors.links}</p> : null}

      <div className="mt-5 space-y-5 border-t border-rule-soft pt-5">
        <div className="rounded-box bg-subtle p-3">
          <p className="text-xs font-medium font-mono tracking-wide text-fg-2 uppercase">
            Home page link
          </p>

          {/*
           * Target and anchor sit side by side, in the same `gap-3
           * sm:grid-cols-2` the secondary rows use, so the two kinds of block
           * match at every width (change order of 5 Sep 2026, round 3). Each
           * field's error stays inside its own column, and since C-61 the
           * secondary rows do the same.
           */}
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label
                htmlFor={`${groupId}-home-url`}
                className="block text-xs font-medium text-fg-2"
              >
                Target URL
              </label>
              <input
                {...NO_AUTOFILL}
                id={`${groupId}-home-url`}
                name="homeLinkUrl"
                value={homeUrlOf(value, mainUrl)}
                onChange={(event) => onChange({ ...value, homeUrl: event.target.value })}
                /*
                 * Shown only while the field is empty, which on a fresh form
                 * means the Main URL is empty too. It describes the tracking
                 * rather than changing it: an empty target here is correct, not
                 * a defect. Operator-approved wording, 5 Sep 2026.
                 */
                placeholder="Follows the main URL above"
                aria-invalid={errors?.homeUrl ? true : undefined}
                className={INPUT_CLASS}
              />
              {errors?.homeUrl ? (
                <p className="mt-1.5 text-sm text-danger">{errors.homeUrl}</p>
              ) : null}
            </div>

            <div>
              <label
                htmlFor={`${groupId}-home-wording`}
                className="block text-xs font-medium text-fg-2"
              >
                Anchor text
              </label>
              <input
                {...NO_AUTOFILL}
                id={`${groupId}-home-wording`}
                name="homeLinkWording"
                maxLength={MAX_LINK_WORDING}
                value={value.homeWording}
                onChange={(event) => onChange({ ...value, homeWording: event.target.value })}
                placeholder={targetName.trim() === '' ? 'The target name' : targetName.trim()}
                aria-invalid={errors?.homeWording ? true : undefined}
                className={INPUT_CLASS}
              />
              {errors?.homeWording ? (
                <p className="mt-1.5 text-sm text-danger">{errors.homeWording}</p>
              ) : null}
            </div>
          </div>
        </div>

        {value.secondary.map((row, index) => (
          <div key={index} className="rounded-box bg-subtle p-3">
            <div className="flex items-baseline justify-between gap-3">
              <p className="text-xs font-medium font-mono tracking-wide text-fg-2 uppercase">
                Another link
              </p>
              <button
                type="button"
                onClick={() =>
                  onChange({
                    ...value,
                    secondary: value.secondary.filter((_, i) => i !== index),
                  })
                }
                className="text-sm font-medium text-fg-muted underline hover:text-fg"
              >
                Remove
              </button>
            </div>

            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor={`${groupId}-url-${index}`}
                  className="block text-xs font-medium text-fg-2"
                >
                  Target URL
                </label>
                <input
                  {...NO_AUTOFILL}
                  id={`${groupId}-url-${index}`}
                  name={`secondaryLinkUrl-${index}`}
                  value={row.url}
                  onChange={(event) => updateRow(index, { url: event.target.value })}
                  placeholder="https://harbourpointplumbing.example/emergency-callouts"
                  aria-invalid={errors?.secondary?.[index]?.url ? true : undefined}
                  className={INPUT_CLASS}
                />
                {errors?.secondary?.[index]?.url ? (
                  <p className="mt-1.5 text-sm text-danger">{errors.secondary[index].url}</p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor={`${groupId}-wording-${index}`}
                  className="block text-xs font-medium text-fg-2"
                >
                  Anchor text
                </label>
                <input
                  {...NO_AUTOFILL}
                  id={`${groupId}-wording-${index}`}
                  name={`secondaryLinkWording-${index}`}
                  maxLength={MAX_LINK_WORDING}
                  value={row.wording}
                  onChange={(event) => updateRow(index, { wording: event.target.value })}
                  placeholder="emergency callouts"
                  aria-invalid={errors?.secondary?.[index]?.wording ? true : undefined}
                  className={INPUT_CLASS}
                />
                {errors?.secondary?.[index]?.wording ? (
                  <p className="mt-1.5 text-sm text-danger">{errors.secondary[index].wording}</p>
                ) : null}
              </div>
            </div>

            <p className="mt-2 text-sm text-fg-muted">Placed on a suitable inside page.</p>
          </div>
        ))}

        {atCap ? (
          <p className="text-sm text-fg-muted">
            That is {MAX_LINKS_PER_SITE} links, which is the most one site carries.
          </p>
        ) : (
          <button
            type="button"
            onClick={() =>
              onChange({ ...value, secondary: [...value.secondary, emptySecondaryRow()] })
            }
            className="text-sm font-medium text-fg underline"
          >
            Add another link
          </button>
        )}

        {/*
         * Operator-approved wording, 5 Sep 2026, moved to the foot of the
         * section on 5 Sep 2026 (round 2) where it reads better. It is a
         * warning, not a rule: the form still accepts a keyword anchor on the
         * home page link, and says whose risk that is. Do not reword without a
         * ruling.
         */}
        <p className="rounded-box bg-subtle px-4 py-3 text-sm text-fg-2">
          For links pointing directly at a client or project website, we recommend using branded
          anchor text or the plain URL only. In our experience, relevance passes through the branded
          link and does the heavy lifting. Keyword anchors from a strongly relevant site can set
          that site&rsquo;s rankings back for weeks and are better saved for linking to assets other
          than the main website. If you point keyword anchors at a main website, you do so at your
          own risk.
        </p>
      </div>
    </fieldset>
  );
}
